import { useEffect, useState } from "react";
import { MembershipDisclaimers } from "@models/user-account-globals";
import { Sanity } from "@models/sanity.model";
import {
  SubscriptionProvider,
  SubscriptionTier,
  SubscriptionType,
  subscriptionTypes,
  useUserProfile,
} from "@providers/profile";
import PortableText from "@components/portable-text";
import { DigitalDetails } from "../Account.styled";

const SubscriptionDigital = ({
  digitalDisclaimers,
}: {
  digitalDisclaimers: MembershipDisclaimers;
}) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<String>();
  const [subscriptionType, setSubscriptionType] = useState<SubscriptionType>();
  const [manageDisclaimer, setManageDisclaimer] = useState<Sanity.PortableText>();
  const {
    profileState: { profile: { subscription_history: subHistory = [] } = {} },
  } = useUserProfile();

  useEffect(() => {
    if (Array.isArray(subHistory) && subHistory.length) {
      // Filter out non-digital subs
      const digitalSubs = subHistory.filter((subHistory) => {
        return (
          subHistory.level === SubscriptionTier.APP_MONTHLY ||
          subHistory.level === SubscriptionTier.APP_ANNUAL
        );
      });

      // Either get the currently active digital sub or default to the first digital sub in history
      const displaySub = digitalSubs.find((sub) => sub.is_active) || digitalSubs?.[0];

      if (displaySub?.is_active !== undefined) {
        setSubscriptionStatus(displaySub?.is_active ? "Active" : "Inactive");
      }

      if (displaySub?.level) {
        setSubscriptionType(subscriptionTypes[displaySub.level]);
      }

      if (displaySub?.provider) {
        let disclaimer;
        switch (displaySub.provider) {
          case SubscriptionProvider.APPLE:
            disclaimer = digitalDisclaimers?.manage_disclaimer_apple;
            break;
          case SubscriptionProvider.GOOGLE:
            disclaimer = digitalDisclaimers?.manage_disclaimer_google;
            break;
        }
        disclaimer && setManageDisclaimer(disclaimer);
      }
    }
  }, [subHistory, digitalDisclaimers]);

  return (
    <DigitalDetails>
      <dt>Subscription Status</dt>
      <dd data-testid="subscription-status-text">{subscriptionStatus}</dd>
      <dt>Subscription Type</dt>
      <dd data-testid="subscription-type-text">{subscriptionType}</dd>
      {manageDisclaimer && (
        <>
          <dt className="disclaimer" data-testid="manage-membership-disclaimer-header">
            Manage/Cancel Membership
          </dt>
          <dd className="disclaimer" data-testid="manage-membership-disclaimer-text">
            <PortableText value={manageDisclaimer} />
          </dd>
        </>
      )}
      {digitalDisclaimers?.upgrade_disclaimer && (
        <>
          <dt className="disclaimer" data-testid="upgrade-membership-disclaimer-header">
            Upgrade Membership
          </dt>
          <dd className="disclaimer" data-testid="upgrade-membership-disclaimer-text">
            <PortableText value={digitalDisclaimers.upgrade_disclaimer} />
          </dd>
        </>
      )}
    </DigitalDetails>
  );
};

export default SubscriptionDigital;
